/**
 * Token storage — the single place that decides WHERE the app's JWTs live.
 *
 * Login's "Remember me" checkbox used to be decorative: it was never registered
 * with the form and never read, so every session was written to `localStorage`
 * and survived a browser restart whether the user asked for that or not. This
 * module makes the choice real:
 *
 * - **Remembered** → `access_token` / `refresh_token` in `localStorage`, so the
 *   session outlives the browser (the historical behaviour).
 * - **Not remembered** → the same keys in `sessionStorage`, so the session dies
 *   with the tab. Nothing is left behind on a shared machine.
 *
 * The `remember_me` flag itself always lives in `localStorage` — it has to
 * outlive the tab, because it is what tells the *next* boot which store to
 * write to.
 *
 * Two rules keep this from logging people out:
 *
 * 1. **Reads never trust the flag.** `getAccessToken()` / `getRefreshToken()`
 *    check `sessionStorage` first and fall back to `localStorage`. Users who
 *    were already signed in when this shipped have tokens in `localStorage`
 *    with no flag set, and they must stay signed in; the same fallback covers a
 *    user who flips the checkbox between sessions.
 * 2. **Writes clear the other store.** `setTokens()` removes the keys from the
 *    backend it is *not* using, so switching from remembered to not-remembered
 *    can't leave a stale `localStorage` token that resurrects a dead session
 *    after the tab closes.
 *
 * Every access is wrapped in try/catch and degrades to an in-memory map:
 * private-mode Safari, sandboxed iframes and quota-exhausted profiles throw on
 * `setItem` (sometimes on mere property access). A storage failure must cost
 * the user persistence across reloads, never a white screen.
 *
 * Nothing outside this module should touch the token keys directly — see
 * `lib/api.ts` (interceptors) and `stores/authStore.ts` (login/logout).
 */

const ACCESS_KEY = 'access_token'
const REFRESH_KEY = 'refresh_token'
const REMEMBER_KEY = 'remember_me'

/** The Zustand `persist` key for `authStore` — cleared alongside the tokens. */
const AUTH_STATE_KEY = 'auth-storage'

type Backend = 'session' | 'local'

/** Last-resort store when the Web Storage APIs are unavailable or throw. */
const memory: Record<Backend, Map<string, string>> = {
  session: new Map(),
  local: new Map(),
}

/** Probe result per backend — computed once, not on every request interceptor. */
const usable: Record<Backend, boolean | null> = { session: null, local: null }

function store(kind: Backend): Storage | null {
  if (usable[kind] === null) {
    usable[kind] = false
    try {
      const s = kind === 'local' ? window.localStorage : window.sessionStorage
      // Safari's private mode hands back a Storage object that throws on the
      // first write rather than on access, so probe with a real round-trip.
      const probe = '__token_storage_probe__'
      s.setItem(probe, '1')
      s.removeItem(probe)
      usable[kind] = true
    } catch {
      usable[kind] = false
    }
  }
  if (!usable[kind]) return null
  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage
  } catch {
    return null
  }
}

function read(kind: Backend, key: string): string | null {
  const s = store(kind)
  if (s) {
    try {
      const value = s.getItem(key)
      if (value !== null) return value
    } catch {
      // fall through to the in-memory mirror
    }
  }
  return memory[kind].get(key) ?? null
}

function write(kind: Backend, key: string, value: string): void {
  const s = store(kind)
  if (s) {
    try {
      s.setItem(key, value)
      memory[kind].delete(key)
      return
    } catch {
      // quota exceeded / private mode — keep the value for this page load only
    }
  }
  memory[kind].set(key, value)
}

function remove(kind: Backend, key: string): void {
  const s = store(kind)
  if (s) {
    try {
      s.removeItem(key)
    } catch {
      // ignore — the in-memory delete below is still authoritative
    }
  }
  memory[kind].delete(key)
}

/**
 * Where a *new* session should be written. Absent flag means "signed in before
 * Remember me existed" (or never signed in), and those tokens live in
 * `localStorage` — so the safe default is `true`.
 */
export function isRemembered(): boolean {
  const raw = read('local', REMEMBER_KEY)
  if (raw === null) return true
  return raw === 'true'
}

/**
 * Record the user's Remember-me choice. Call this BEFORE the login request so
 * the response's tokens are written to the right backend.
 */
export function setRememberMe(remember: boolean): void {
  write('local', REMEMBER_KEY, remember ? 'true' : 'false')
}

function activeBackend(): Backend {
  return isRemembered() ? 'local' : 'session'
}

function otherBackend(kind: Backend): Backend {
  return kind === 'local' ? 'session' : 'local'
}

/** Where the *live* session actually is, regardless of what the flag says. */
function currentBackend(): Backend {
  if (read('session', ACCESS_KEY) !== null || read('session', REFRESH_KEY) !== null) return 'session'
  if (read('local', ACCESS_KEY) !== null || read('local', REFRESH_KEY) !== null) return 'local'
  return activeBackend()
}

export function getAccessToken(): string | null {
  return read('session', ACCESS_KEY) ?? read('local', ACCESS_KEY)
}

export function getRefreshToken(): string | null {
  return read('session', REFRESH_KEY) ?? read('local', REFRESH_KEY)
}

/** Persist a freshly minted token pair (login, register, SSO handoff). */
export function setTokens(access: string, refresh: string): void {
  const kind = activeBackend()
  const stale = otherBackend(kind)
  remove(stale, ACCESS_KEY)
  remove(stale, REFRESH_KEY)
  write(kind, ACCESS_KEY, access)
  write(kind, REFRESH_KEY, refresh)
}

/**
 * Replace only the access token (the refresh-token rotation in the axios
 * response interceptor). Writes back to whichever store is already holding the
 * session so a refresh can never promote a tab-scoped session into
 * `localStorage`.
 */
export function setAccessToken(access: string): void {
  write(currentBackend(), ACCESS_KEY, access)
}

/**
 * Persist a rotated PAIR from `/auth/refresh-token`, which now returns a fresh
 * refresh token alongside the access token so an active session slides forward
 * instead of expiring a fixed number of days after sign-in.
 *
 * Deliberately different from `setTokens()` in two ways:
 *
 * - It writes to `currentBackend()` (where the live session actually is), not
 *   `activeBackend()` (what the Remember-me flag says), for the same reason
 *   `setAccessToken()` does: a refresh must never promote a tab-scoped session
 *   into `localStorage`.
 * - It does NOT clear the other backend. `setTokens()` can, because a fresh
 *   sign-in legitimately supersedes whatever was there. A rotation can't:
 *   `sessionStorage` is per-tab, so a second tab holding an unremembered
 *   session would silently evict a remembered session in the first tab (or
 *   vice versa) every time it refreshed.
 */
export function setTokenPair(access: string, refresh: string): void {
  const kind = currentBackend()
  write(kind, ACCESS_KEY, access)
  write(kind, REFRESH_KEY, refresh)
}

/**
 * Wipe every trace of the session from BOTH backends — tokens, the Zustand
 * `auth-storage` snapshot and the Remember-me flag. Clearing only the active
 * backend would leave a half-session behind that the session-first read order
 * could pick back up.
 *
 * Callers that also reset the Zustand store must call this AFTER their `set()`,
 * or the persist middleware immediately rewrites `auth-storage`.
 */
export function clearTokens(): void {
  const backends: Backend[] = ['session', 'local']
  for (const kind of backends) {
    remove(kind, ACCESS_KEY)
    remove(kind, REFRESH_KEY)
    remove(kind, AUTH_STATE_KEY)
  }
  remove('local', REMEMBER_KEY)
}

/**
 * `StateStorage` adapter for Zustand's `persist`, so the cached
 * `user` / `isAuthenticated` snapshot follows the tokens.
 *
 * Without this, an unremembered session leaves an `auth-storage` entry in
 * `localStorage` after the tab closes: the next boot hydrates an authenticated
 * shell with no token behind it, renders the app, then bounces to /login on the
 * first 401. Reads use the same session-first fallback so existing persisted
 * state still hydrates.
 */
export const authStateStorage = {
  getItem: (name: string): string | null => read('session', name) ?? read('local', name),
  setItem: (name: string, value: string): void => {
    const kind = activeBackend()
    remove(otherBackend(kind), name)
    write(kind, name, value)
  },
  removeItem: (name: string): void => {
    remove('session', name)
    remove('local', name)
  },
}
